import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Lodash
import isEmpty from 'lodash/isEmpty';
import reverse from 'lodash/reverse';
import has from 'lodash/has';

// Date-fns
import Format from 'date-fns/format';

import {
    Flex,
    Spacer,
    Text,
    Table,
} from '@zillow/constellation';

import {
    Container,
    Col,
    Row,
} from '@zillow/zrs-comet';

// Actions
import StatisticsActions from 'app/shared/actions/StatisticsActions';

import AsideAnalytics from 'app/shared/components/AsideAnalytics';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import sharedConfig from 'app/shared/sharedConfig';
import PDPContentSkeleton from 'app/shared/components/skeletons/PDPContentSkeleton';
import CustomTable from 'app/shared/components/CustomTable';
import HeaderTooltip from 'app/shared/components/Analytics/HeaderTooltip';

// Selectors
import { UserSelectors } from 'app/shared/selectors';

import { RMWContext } from 'app/shared/providers/RMWContext';

const DEFAULT_PAGE_TITLE = 'All Your Listings';

const propTypes = {
    match: PropTypes.object.isRequired,
};

class StatisticsPage extends React.Component {
    static contextType = RMWContext;

    static contextTypes = {
        paths: PropTypes.object,
    };

    constructor(props) {
        super(props);

        this.state = this.getInitialState();

        this.getStatisticsData = this.getStatisticsData.bind(this);
        this.renderLoadComplete = this.renderLoadComplete.bind(this);
        this.renderStatsTables = this.renderStatsTables.bind(this);
        this.renderHeaders = this.renderHeaders.bind(this);
        this.getTableData = this.getTableData.bind(this);
    }

    getInitialState() {
        return {
            isLoading: false,
            pageTitle: DEFAULT_PAGE_TITLE,
            statisticsData: {},
            listingId: this.props.match.params.propertyId, // endpoint for stats is listingId
        };
    }

    componentWillMount() {
        this.setState({ isLoading: true });
    }

    componentDidMount() {
        this.getStatisticsData(this.state.listingId);
    }

    getStatisticsData(listingId) {
        const params = {};

        if (listingId) {
            params.listingId = listingId;

            this.setState({ listingId });
        }

        this.props.dispatch(StatisticsActions.getStats(params))
            .then((result) => {
                const data = result.payload;

                if (data.success === false) {
                    this.setState({
                        isLoading: false,
                        error: data.error.message,
                    });
                } else {
                    if (data.listingTitle) {
                        this.setState({
                            pageTitle: `${data.listingTitle}`,
                        });
                    } else if (data.singleListing) {
                        this.setState({
                            pageTitle: 'Your Listing',
                        });
                    }

                    this.setState({
                        isLoading: false,
                        statisticsData: data,
                    });
                }
            });
    }

    getHeaderList(date, hasBillableData) {
        const headerList = [
            date,
            'LISTING VIEWS',
            'PHONE LEADS',
            'EMAIL LEADS',
            'TOTAL LEADS',
        ];

        if (hasBillableData) {
            headerList.push('UNIQUE CLICKS');
            headerList.push('UNIQUE LEADS');
        }

        const toolTipMap = hasBillableData ? {
            'UNIQUE CLICKS': 'Number of unique clicks this property received in a day',
            'UNIQUE LEADS': 'Number of unique leads this property received in a day',
        } : {};

        return (
            <Table.Header>
                <Table.Row>
                    {headerList.map((headerItem, index) => (
                        <Table.HeaderCell key={index}>
                            {has(toolTipMap, headerItem)
                                ? <HeaderTooltip title={headerItem} message={toolTipMap[headerItem]} />
                                : headerItem
                            }
                        </Table.HeaderCell>
                    ))}
                </Table.Row>
            </Table.Header>
        );
    }

    getRowList(data, isDailyTable, hasBillableData) {
        const statistics = Object.values(data);
        return reverse(statistics).map((stat) => {
            // Month data is in 'MM-YYYY' format and date-fns doesn't know how to process it.
            // Add a fake day to the month-year string so we can format the date string
            const monthDayYear = `${stat.displayedDate.slice(0, 3)}01-${stat.displayedDate.slice(3)}`;
            // @TODO Temporary fix: date-fns needs ISO-8601 strings
            // Waiting for backend to return a date object or ISO-8601 string
            const monthDayYearToShuffle = isDailyTable ? stat.displayedDate : monthDayYear;
            const monthDayYearArray = monthDayYearToShuffle.split('-');
            const yearMonthDay = [monthDayYearArray[2], monthDayYearArray[0], monthDayYearArray[1]].join('-');
            const format = isDailyTable ? 'MMM DD' : 'MMM YY';
            const date = Format(yearMonthDay, format);

            const rowData = hasBillableData ? {
                date,
                listingViewed: stat.totalViewed,
                phoneLeads: stat.totalPhoneLeads,
                emailLeads: stat.totalInquiries,
                totalLeads: stat.totalPhoneLeads + stat.totalInquiries,
                uniqueClicks: stat.totalBillableClicks < 0 ? 0 : stat.totalBillableClicks,
                uniqueLeads: stat.totalBillableLeads < 0 ? 0 : stat.totalBillableLeads,
            } : {
                date,
                listingViewed: stat.totalViewed,
                phoneLeads: stat.totalPhoneLeads,
                emailLeads: stat.totalInquiries,
                totalLeads: stat.totalPhoneLeads + stat.totalInquiries,
            };

            return (
                <Table.Row key={rowData.date}>
                    {Object.keys(rowData).map(key => (
                        <Table.HeaderCell key={key}>
                            {rowData[key]}
                        </Table.HeaderCell>
                    ))}
                </Table.Row>
            );
        });
    }

    getFooter(data, hasBillableData) {
        const rowData = hasBillableData ? {
            date: 'Past 31 days',
            listingViewed: data.totalViewed,
            phoneLeads: data.totalPhoneLeads,
            emailLeads: data.totalInquiries,
            totalLeads: data.totalPhoneLeads + data.totalInquiries,
            uniqueClicks: data.totalBillableClicks < 0 ? 0 : data.totalBillableClicks,
            uniqueLeads: data.totalBillableLeads < 0 ? 0 : data.totalBillableLeads,
        } : {
            date: 'Past 31 days',
            listingViewed: data.totalViewed,
            phoneLeads: data.totalPhoneLeads,
            emailLeads: data.totalInquiries,
            totalLeads: data.totalPhoneLeads + data.totalInquiries,
        };

        return (
            <Table.Row>
                {Object.keys(rowData).map(key => (
                    <Table.HeaderCell key={key}>
                        <b>
                            {rowData[key]}
                        </b>
                    </Table.HeaderCell>
                ))}
            </Table.Row>
        );
    }

    getTableData(statisticModel, attribute, isDailyTable, showMonthlySummary, hasBillableData) {
        const tableData = {
            headerList: this.getHeaderList(isDailyTable ? 'DATE' : 'MONTH', hasBillableData),
            rowList: this.getRowList(statisticModel[attribute], isDailyTable, hasBillableData),
            errorMessage: 'There were no traffic records found for your listings.' +
            ' They may take around an hour to show up here.',
        };

        tableData.footer = showMonthlySummary
            ? this.getFooter(statisticModel.dailyListingStats, hasBillableData)
            : null;

        return tableData;
    }

    renderHeaders(dailyListingStats) {
        return (
            <Flex display="flex">
                <Spacer paddingBottom="md" paddingRight="md">
                    <Text as="div" fontType="bodyHeading">
                        {`${dailyListingStats.totalInquiries + dailyListingStats.totalPhoneLeads} total leads`}
                    </Text>
                    <Text as="div" fontType="bodySmall">
                        Past 31 days
                    </Text>
                </Spacer>

                <Spacer paddingBottom="md" paddingRight="md">
                    <Text as="div" fontType="bodyHeading">
                        {`${dailyListingStats.totalViewed} total views`}
                    </Text>
                    <Text as="div" fontType="bodySmall">
                        Past 31 days
                    </Text>
                </Spacer>
            </Flex>
        );
    }

    renderStatsTables() {
        // statistic model
        const statisticModel = this.state.statisticsData;
        const { dailyListingStats } = statisticModel;
        const isEmptyDaily = isEmpty(statisticModel.dailyListingStatsByYyyyMmDd);
        const isEmptyMonthly = isEmpty(statisticModel.monthlyListingStatsByYyyyMm);
        const hasDailyBillableData = statisticModel.dailyBillableCountsAvailable;
        const hasMonthlyBillableData = statisticModel.monthlyBillableCountsAvailable;
        const monthlySummary = statisticModel && !isEmptyDaily;

        return (
            <div>
                {monthlySummary ? this.renderHeaders(dailyListingStats) : null }
                <Spacer paddingBottom="md">
                    <Text fontType="h4">
                        Events by Day
                    </Text>
                    <CustomTable
                        tableData={
                            this.getTableData(statisticModel,
                                'dailyListingStatsByYyyyMmDd',
                                true,
                                monthlySummary,
                                hasDailyBillableData)}
                        className="StatisticsPage-table-daily"
                    />
                </Spacer>

                {!statisticModel.monthlySummariesUnavailable && !isEmptyMonthly ? (
                    <Spacer paddingBottom="md">
                        <Text fontType="h4">
                            Events by Month
                        </Text>
                        <CustomTable
                            tableData={
                                this.getTableData(statisticModel,
                                    'monthlyListingStatsByYyyyMm',
                                    false,
                                    false,
                                    hasMonthlyBillableData)}
                            className="StatisticsPage-table-monthly"
                        />
                    </Spacer>
                ) : null }
            </div>
        );
    }

    renderLoadComplete() {
        const { hideAsideAnalytics } = this.props;
        const { paths } = this.context;

        const listingIdParam = this.state.listingId ? `?listingId=${this.state.listingId}` : '';
        const excelPath = paths.exportExcel.statistics;
        const excelUrl = `${paths.proxy}/${sharedConfig.baseApi}${excelPath}${listingIdParam}`;

        return (
            <Container>
                <Row>
                    <Col xs={12} lg={9}>
                        {this.state.error
                            ? (
                                <Text className="error">
                                    {this.state.error}
                                </Text>
                            ) : this.renderStatsTables()
                        }
                    </Col>

                    {!this.state.error && (
                        <Col xs={12} lg={3}>
                            {!hideAsideAnalytics && (
                                <AsideAnalytics
                                    buttonText="Download Stats"
                                    excelUrl={excelUrl}
                                />
                            )}
                        </Col>
                    )}
                </Row>
            </Container>
        );
    }

    render() {
        const pageTitleContent = `Statistics for ${this.state.pageTitle}`;

        return (
            <div className="StatisticsPage">
                <HeadWrapper title={pageTitleContent} />
                {this.state.isLoading ? <PDPContentSkeleton /> : this.renderLoadComplete()}
            </div>
        );
    }
}

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
});

StatisticsPage.propTypes = propTypes;

export { StatisticsPage };
export default connect(mapStateToProps)(StatisticsPage);
